import React, { useState } from 'react';

const Interviews = () => {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Mock data - in real app, this would come from API
  const interviewsData = {
    upcoming: [
      {
        id: 1,
        company: 'TechCorp Inc.',
        position: 'Senior Frontend Developer',
        date: '2024-01-15',
        time: '14:30',
        type: 'Technical Round',
        interviewer: 'Sarah Johnson (Tech Lead)',
        duration: '60 mins',
        location: 'Google Meet',
        status: 'scheduled',
        preparation: {
          materials: ['React fundamentals', 'System design', 'JavaScript concepts'],
          notes: 'Focus on performance optimization and React hooks'
        }
      },
      {
        id: 2,
        company: 'StartUpXYZ',
        position: 'Full Stack Developer',
        date: '2024-01-18',
        time: '11:00',
        type: 'Cultural Fit',
        interviewer: 'Mike Chen (CEO)',
        duration: '45 mins',
        location: 'Zoom',
        status: 'scheduled',
        preparation: {
          materials: ['Company values', 'Project experience', 'Teamwork examples'],
          notes: 'Be prepared to discuss previous startup experience'
        }
      }
    ],
    completed: [
      {
        id: 3,
        company: 'Enterprise Solutions',
        position: 'React Developer',
        date: '2024-01-10',
        time: '10:00',
        type: 'Final Round',
        interviewer: 'David Wilson (Engineering Manager)',
        duration: '90 mins',
        location: 'Microsoft Teams',
        status: 'completed',
        feedback: {
          rating: 4,
          strengths: ['Strong React knowledge', 'Good problem-solving skills'],
          improvements: ['Could improve on system design questions'],
          nextSteps: 'HR will contact within 3 days',
          outcome: 'pending'
        }
      },
      {
        id: 4,
        company: 'Digital Innovations',
        position: 'Frontend Engineer',
        date: '2024-01-05',
        time: '15:30',
        type: 'Technical Screening',
        interviewer: 'Emily Roberts (Senior Developer)',
        duration: '45 mins',
        location: 'Phone Call',
        status: 'completed',
        feedback: {
          rating: 3,
          strengths: ['Clean code', 'Good communication'],
          improvements: ['Need more practice with algorithms'],
          nextSteps: 'Not selected for next round',
          outcome: 'rejected'
        }
      }
    ],
    canceled: [
      {
        id: 5,
        company: 'Web Masters',
        position: 'UI Developer',
        date: '2024-01-12',
        time: '13:00',
        type: 'Initial Screening',
        interviewer: 'Lisa Brown (HR)',
        duration: '30 mins',
        location: 'Phone Call',
        status: 'canceled',
        reason: 'Position put on hold by company'
      }
    ]
  };

  const stats = {
    total: 8,
    upcoming: 2,
    completed: 4,
    offers: 1,
    cancelations: 2
  };

  const handleAddFeedback = (interview) => {
    setSelectedInterview(interview);
    setShowFeedbackModal(true);
  };

  const handleJoinInterview = (interview) => {
    window.open(`https://meet.google.com/new`, '_blank');
  };

  const handleReschedule = (interview) => {
    alert(`Reschedule functionality for ${interview.company} would open here`);
  };

  const getStatusBadge = (status, outcome) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'scheduled':
        return <span className={`${baseClasses} bg-blue-100 text-blue-800`}>Scheduled</span>;
      case 'completed':
        if (outcome === 'accepted') {
          return <span className={`${baseClasses} bg-green-100 text-green-800`}>Offer Received</span>;
        } else if (outcome === 'rejected') {
          return <span className={`${baseClasses} bg-red-100 text-red-800`}>Not Selected</span>;
        } else {
          return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Pending</span>;
        }
      case 'canceled':
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Canceled</span>;
      default:
        return <span className={`${baseClasses} bg-gray-100 text-gray-800`}>{status}</span>;
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`fas fa-star ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
      ></i>
    ));
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Interviews</h1>
        <p className="text-gray-400">Manage your interview schedule and track your progress</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <i className="fas fa-calendar-check text-blue-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Interviews</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-clock text-green-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-bold text-gray-900">{stats.upcoming}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-trophy text-purple-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Offers</p>
              <p className="text-2xl font-bold text-gray-900">{stats.offers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <i className="fas fa-times-circle text-red-500 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Canceled</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cancelations}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-md mt-8">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {['upcoming', 'completed', 'canceled'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)} 
                <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2 rounded-full text-xs">
                  {interviewsData[tab].length}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Interview List */}
        <div className="p-6">
          {interviewsData[activeTab].length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-calendar-times text-gray-400 text-5xl mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {activeTab} interviews</h3>
              <p className="text-gray-500">
                {activeTab === 'upcoming' 
                  ? "You don't have any upcoming interviews scheduled."
                  : `You don't have any ${activeTab} interviews.`
                }
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {interviewsData[activeTab].map((interview) => (
                <div key={interview.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{interview.position}</h3>
                          <p className="text-gray-400">{interview.company}</p>
                        </div>
                        {getStatusBadge(interview.status, interview.feedback?.outcome)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center text-gray-600">
                          <i className="fas fa-calendar text-gray-400 mr-2"></i>
                          <span>{new Date(interview.date).toLocaleDateString()} at {interview.time}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <i className="fas fa-clock text-gray-400 mr-2"></i>
                          <span>{interview.duration}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <i className="fas fa-user text-gray-400 mr-2"></i>
                          <span>{interview.interviewer}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <i className="fas fa-video text-gray-400 mr-2"></i>
                          <span>{interview.location}</span>
                        </div>
                      </div>

                      {/* Additional Information based on tab */}
                      {activeTab === 'upcoming' && interview.preparation && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                            <i className="fas fa-lightbulb mr-2"></i>
                            Preparation Materials
                          </h4>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {interview.preparation.materials.map((material, index) => (
                              <span key={index} className="bg-white text-blue-700 px-2 py-1 rounded text-sm">
                                {material}
                              </span>
                            ))}
                          </div>
                          {interview.preparation.notes && (
                            <p className="text-blue-800 text-sm">{interview.preparation.notes}</p>
                          )}
                        </div>
                      )}

                      {activeTab === 'completed' && interview.feedback && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium text-gray-900">Interview Feedback</h4>
                            <div className="flex items-center">
                              {renderStars(interview.feedback.rating)}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <h5 className="font-medium text-green-600 mb-1">Strengths</h5>
                              <ul className="text-gray-600">
                                {interview.feedback.strengths.map((strength, index) => (
                                  <li key={index} className="flex items-center">
                                    <i className="fas fa-check text-green-500 mr-2 text-xs"></i>
                                    {strength}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h5 className="font-medium text-orange-600 mb-1">Areas for Improvement</h5>
                              <ul className="text-gray-600">
                                {interview.feedback.improvements.map((improvement, index) => (
                                  <li key={index} className="flex items-center">
                                    <i className="fas fa-arrow-right text-orange-500 mr-2 text-xs"></i>
                                    {improvement}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                          {interview.feedback.nextSteps && (
                            <div className="mt-3 p-3 bg-white rounded border">
                              <h5 className="font-medium text-gray-900 mb-1">Next Steps</h5>
                              <p className="text-gray-600 text-sm">{interview.feedback.nextSteps}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                    {activeTab === 'upcoming' && (
                      <>
                        <button
                          onClick={() => handleJoinInterview(interview)}
                          className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                        >
                          <i className="fas fa-video"></i>
                          Join Interview
                        </button>
                        <button
                          onClick={() => handleReschedule(interview)}
                          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          <i className="fas fa-calendar-alt"></i>
                          Reschedule
                        </button>
                        <button className="flex items-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors">
                          <i className="fas fa-times"></i>
                          Cancel
                        </button>
                      </>
                    )}
                    {activeTab === 'completed' && (
                      <button
                        onClick={() => handleAddFeedback(interview)}
                        className="flex items-center gap-2 bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
                      >
                        <i className="fas fa-edit"></i>
                        Add Feedback
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions & Resources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Interview Preparation */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Interview Preparation</h3>
          <div className="space-y-3">
            <button className="w-full flex items-center gap-3 p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <i className="fas fa-file-alt text-blue-500"></i>
              <div>
                <p className="font-medium text-gray-800">Common Questions</p>
                <p className="text-sm text-gray-600">Practice typical interview questions</p>
              </div>
            </button>
            <button className="w-full flex items-center gap-3 p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <i className="fas fa-code text-green-500"></i>
              <div>
                <p className="font-medium text-gray-800">Coding Challenges</p>
                <p className="text-sm text-gray-600">Sharpen your technical skills</p>
              </div>
            </button>
            <button className="w-full flex items-center gap-3 p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <i className="fas fa-video text-purple-500"></i>
              <div>
                <p className="font-medium text-gray-800">Mock Interviews</p>
                <p className="text-sm text-gray-600">Practice with simulated interviews</p>
              </div>
            </button>
          </div>
        </div>

        {/* Interview Statistics */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Interview Statistics</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Success Rate</span>
                <span className="font-medium text-gray-800">25%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: '25%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Average Rating</span>
                <span className="font-medium text-gray-800">3.8/5</span>
              </div>
              <div className="flex gap-1">
                {renderStars(3.8)}
              </div>
            </div>
            <div className="text-center pt-4">
              <button className="text-blue-500 hover:text-blue-700 text-sm font-medium">
                <i className="fas fa-chart-bar mr-2"></i>
                View Detailed Analytics
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Add Interview Feedback</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating</label>
                  <div className="flex gap-1 text-2xl">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} className="text-yellow-400 hover:text-yellow-500">
                        <i className="fas fa-star"></i>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Strengths</label>
                  <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" rows="3"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Areas for Improvement</label>
                  <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" rows="3"></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Outcome</label>
                  <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="pending">Pending</option>
                    <option value="accepted">Offer Received</option>
                    <option value="rejected">Not Selected</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors">
                  Save Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Interviews;